import esriConfig from '@arcgis/core/config';
import type { Store } from '@reduxjs/toolkit';
import { baseApi } from './base.service';

const DEFAULT_EXPIRES_IN_SECONDS = 3600;
const REFRESH_BUFFER_MS = 5 * 60 * 1000;

const getCsrfToken = () => {
  if (typeof document === 'undefined') {
    return null;
  }

  const csrfMetaTag = document.head.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null;

  return csrfMetaTag?.content ?? null;
};

interface TilesTokenResponse {
  access_token: string;
  expires_in: number;
}

interface TokenActionResult {
  unwrap: () => Promise<TilesTokenResponse>;
}

interface InterceptorParams {
  requestOptions?: {
    headers?: Record<string, string>;
  } & Record<string, unknown>;
}

interface InterceptorError {
  details?: {
    httpStatus?: number;
  };
}

let storeRef: Store | null = null;
let tokenCache: string | null = null;
let tokenExpiresAtMs = 0;
let tokenRequestPromise: Promise<string> | null = null;
let tokenGeneration = 0;
let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let interceptorRegistered = false;
let registeredInterceptor: {
  before: (params: InterceptorParams) => Promise<void>;
  error: (error: InterceptorError) => void;
  urls: string | string[];
} | null = null;

const normalizeInterceptorUrls = (tileUrls: string | string[]) => {
  const uniqueUrls = Array.from(new Set((Array.isArray(tileUrls) ? tileUrls : [tileUrls])
    .map((url) => url.trim())
    .filter((url) => url.length > 0)));

  if (uniqueUrls.length === 1) {
    return uniqueUrls[0];
  }

  return uniqueUrls;
};

const normalizeExpiresInSeconds = (expiresIn: number | undefined) => {
  if (expiresIn === undefined || !Number.isFinite(expiresIn)) {
    return DEFAULT_EXPIRES_IN_SECONDS;
  }

  return Math.max(1, Math.floor(expiresIn));
};

const clearRefreshTimer = () => {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
};

const hasUsableCachedToken = () => {
  if (!tokenCache) {
    return false;
  }

  return Date.now() < tokenExpiresAtMs - REFRESH_BUFFER_MS;
};

const buildRefreshDelayMs = (expiresInSeconds: number) => {
  const refreshDelayMs = expiresInSeconds * 1000 - REFRESH_BUFFER_MS;

  // Avoid a tight loop for very short TTL values.
  return Math.max(refreshDelayMs, 1000);
};

const scheduleRefresh = (expiresInSeconds: number) => {
  clearRefreshTimer();

  refreshTimer = setTimeout(async () => {
    try {
      await forceRefreshTilesToken();
    } catch {
      // The next tiles request will retry token acquisition.
    }
  }, buildRefreshDelayMs(expiresInSeconds));
};

const ensureStoreRef = () => {
  if (!storeRef) {
    throw new Error('Tiles token store has not been initialized. Call registerTilesInterceptor first.');
  }

  return storeRef;
};

const fetchTilesToken = async (requestGeneration: number) => {
  const store = ensureStoreRef();
  const action = tilesTokenApi.endpoints.getTilesToken.initiate();
  const dispatch = store.dispatch as (pendingAction: unknown) => TokenActionResult;

  const result = await dispatch(action).unwrap();
  const expiresInSeconds = normalizeExpiresInSeconds(result.expires_in);

  if (requestGeneration !== tokenGeneration) {
    return tokenRequestPromise ?? tokenCache ?? forceRefreshTilesToken();
  }

  tokenCache = result.access_token;
  tokenExpiresAtMs = Date.now() + expiresInSeconds * 1000;
  scheduleRefresh(expiresInSeconds);

  return result.access_token;
};

const forceRefreshTilesToken = async () => {
  tokenGeneration += 1;
  const requestGeneration = tokenGeneration;

  tokenRequestPromise = fetchTilesToken(requestGeneration)
    .finally(() => {
      if (requestGeneration === tokenGeneration) {
        tokenRequestPromise = null;
      }
    });

  return tokenRequestPromise;
};

export const tilesTokenApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getTilesToken: build.mutation<TilesTokenResponse, void>({
      query: () => ({
        url: '/api/_private/token',
        method: 'POST',
        headers: {
          ...(getCsrfToken() ? { 'X-CSRF-Token': getCsrfToken() as string } : {}),
          'Content-Type': 'application/json',
        },
        body: {
          aud: 'tiles',
          expires_in: DEFAULT_EXPIRES_IN_SECONDS,
        },
      }),
    }),
  }),
});

export const getTilesToken = async () => {
  if (hasUsableCachedToken()) {
    return tokenCache || '';
  }

  if (tokenRequestPromise !== null) {
    return tokenRequestPromise;
  }

  return forceRefreshTilesToken();
};

export const invalidateTilesToken = () => {
  tokenGeneration += 1;
  tokenCache = null;
  tokenExpiresAtMs = 0;
  tokenRequestPromise = null;
  clearRefreshTimer();
};

export const registerTilesInterceptor = (store: Store, tileUrls: string | string[]) => {
  storeRef = store;

  if (interceptorRegistered) {
    return;
  }

  interceptorRegistered = true;
  const interceptorUrls = normalizeInterceptorUrls(tileUrls);

  registeredInterceptor = {
    urls: interceptorUrls,
    before: async (params) => {
      const token = await getTilesToken();
      const existingHeaders = params.requestOptions?.headers || {};
      const requestOptions = {
        ...params.requestOptions,
        headers: {
          ...existingHeaders,
          Authorization: `Bearer ${token}`,
        },
      };

      Reflect.set(params, 'requestOptions', requestOptions);
    },
    error: (error) => {
      if (error?.details?.httpStatus === 401) {
        invalidateTilesToken();
      }
    },
  };

  esriConfig.request.interceptors?.push(registeredInterceptor);
};

export const __testing = {
  reset: () => {
    storeRef = null;
    tokenCache = null;
    tokenExpiresAtMs = 0;
    tokenRequestPromise = null;
    tokenGeneration = 0;
    interceptorRegistered = false;
    registeredInterceptor = null;
    clearRefreshTimer();
  },
  getRegisteredInterceptor: () => registeredInterceptor,
};

export const {
  useGetTilesTokenMutation,
} = tilesTokenApi;
